import API, { setAuthToken } from "@core/api/api";
import config from "@core/config/env";

/**
 * Enhanced overview API call with better parameter handling and error management
 */
export const getOverview = async (params) => {
  try {
    // Ensure authentication token is set
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("Authentication token not found");
    }
    setAuthToken(token);

    // Validate required parameters
    if (!params) {
      throw new Error("Parameters are required");
    }

    // Make the API call
    const response = await API.get(`/statistics/overview?${params}`);

    // Validate response structure
    if (!response.data || typeof response.data !== "object") {
      throw new Error("Invalid response format from server");
    }

    return response.data;
  } catch (error) {
    // Log error for debugging (only in development)
    if (config.isDevelopment) {
      console.error("Overview API error:", error);
    }

    // Re-throw with enhanced error information
    if (error.response) {
      // Server responded with error status
      const message = error.response.data?.message || "Server error occurred";
      const enhancedError = new Error(message);
      enhancedError.status = error.response.status;
      enhancedError.data = error.response.data;
      throw enhancedError;
    } else if (error.request) {
      // Request was made but no response received
      throw new Error("Network error: Unable to connect to server");
    } else {
      // Something else happened
      throw error;
    }
  }
};

export const getPackageStatusCount = async ({ city, date }) => {
  const seachParams = new URLSearchParams();
  seachParams.append("from", date.from);
  seachParams.append("to", date.to);
  seachParams.append("city", city);

  if (!localStorage.getItem("token")) {
    return null;
  }
  setAuthToken(localStorage.getItem("token"));
  const response = await API.get(`/statistics/packages?${seachParams}`);
  return response.data;
};

export const invoicesStatus = async ({ date }) => {
  if (!localStorage.getItem("token")) {
    return null;
  }
  setAuthToken(localStorage.getItem("token"));
  const seachParams = new URLSearchParams();
  seachParams.append("from", date.from);
  seachParams.append("to", date.to);
  const response = await API.get(`/statistics/invoices?${seachParams}`);
  return response.data;
};

export const getAmounts = async ({ date }) => {
  if (!localStorage.getItem("token")) {
    return null;
  }
  setAuthToken(localStorage.getItem("token"));
  const seachParams = new URLSearchParams();
  seachParams.append("from", date.from);
  seachParams.append("to", date.to);
  const response = await API.get(`/statistics/amounts?${seachParams}`);

  return response.data;
};

export const getPackageStatistics = async () => {
  if (!localStorage.getItem("token")) {
    return null;
  }
  setAuthToken(localStorage.getItem("token"));

  const response = await API.get(`/statistics/overview`);
  return response.data;
};

/**
 * Get user statistics with better error handling
 */
export const getUserStatistics = async () => {
  try {
    const token = localStorage.getItem("token");
    if (!token) {
      throw new Error("Authentication required");
    }

    setAuthToken(token);
    const response = await API.get(`/statistics/user`);
    return response.data;
  } catch (error) {
    console.error("User statistics error:", error);
    throw error;
  }
};
